/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, Cell
} from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface NGOBudgetProps {
  user: User;
}

interface Project {
  id: string;
  name: string;
  budget?: number;
  tranche_plan?: any[];
}

interface FundRequest {
  id: string;
  project_id: string;
  tranche_number: number;
  amount: number;
  purpose: string;
  status: string;
  attachments?: string[];
  clarification_request?: string;
  rejected_reason?: string;
  created_at: string;
  projects?: { name: string };
}

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
});

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280']; // Green, Amber, Red, Gray

const NGOBudget: React.FC<NGOBudgetProps> = ({ user }) => {
  const { userProfile } = useAuth();
  const ngoId = userProfile?.ngo_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [fundRequests, setFundRequests] = useState<FundRequest[]>([]);
  const [receipts, setReceipts] = useState<any[]>([]);
  const [projectExpenditures, setProjectExpenditures] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'budget' | 'requests' | 'receipts'>('overview');

  // Fund Request Form
  const [fundRequestForm, setFundRequestForm] = useState({
    tranche_number: '',
    amount: '',
    purpose: '',
    attachments: [] as File[]
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [showFundRequestModal, setShowFundRequestModal] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);

  // Receipt Acknowledgement Form
  const [receiptForm, setReceiptForm] = useState({
    tranche_number: '',
    date: '',
    amount: '',
    receipt_file: null as File | null
  });
  const [submittingReceipt, setSubmittingReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);


  const fetchData = useCallback(async () => {
    if (!ngoId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch projects assigned to this NGO
      const { data: projectNgos, error: projectNgosError } = await supabase
        .from('project_ngos')
        .select(`
          project_id,
          projects:project_id (
            id,
            name,
            budget,
            tranche_plan
          )
        `)
        .eq('ngo_id', ngoId)
        .in('status', ['accepted', 'pending', 'changes_requested', 'pre_start', 'active']);

      if (projectNgosError) throw projectNgosError;

      const safeProjects: Project[] = (projectNgos || [])
        .filter((pn: any) => pn.projects !== null)
        .map((pn: any) => ({
          id: pn.projects.id,
          name: pn.projects.name,
          budget: pn.projects.budget,
          tranche_plan: pn.projects.tranche_plan
        }));

      setProjects(safeProjects);

      const projectIds = safeProjects.map(p => p.id);

      // Fetch expenditures
      const { data: expendituresData, error: expendituresError } = await supabase
        .from('project_expenditures')
        .select('id, project_id, expenditure_amount, description')
        .in('project_id', projectIds);
      
      if (expendituresError) {
        console.error('Error fetching project expenditures:', expendituresError);
      } else {
        setProjectExpenditures(expendituresData || []);
      }

      // Fetch fund requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('fund_requests')
        .select(`
          id,
          project_id,
          tranche_number,
          amount,
          purpose,
          status,
          attachments,
          clarification_request,
          rejected_reason,
          created_at,
          projects:project_id (
            name
          )
        `)
        .eq('ngo_id', ngoId)
        .order('created_at', { ascending: false });

      if (requestsError) {
        console.error('Error fetching fund requests:', requestsError);
      } else if (requestsData) {
        const mappedRequests: FundRequest[] = requestsData.map((req: any) => ({
          id: req.id,
          project_id: req.project_id,
          tranche_number: req.tranche_number,
          amount: req.amount,
          purpose: req.purpose,
          status: req.status,
          attachments: req.attachments,
          clarification_request: req.clarification_request,
          rejected_reason: req.rejected_reason,
          created_at: req.created_at,
          projects: Array.isArray(req.projects) && req.projects.length > 0
            ? req.projects[0]
            : { name: 'Project' }
        }));
        setFundRequests(mappedRequests);
      }

      // Fetch receipts
      const { data: receiptsData, error: receiptsError } = await supabase
        .from('fund_receipts')
        .select(`
          id,
          project_id,
          tranche_number,
          receipt_date,
          amount,
          receipt_file_url,
          notes,
          uploaded_at,
          projects:project_id (
            name
          )
        `)
        .eq('ngo_id', ngoId)
        .order('uploaded_at', { ascending: false });

      if (receiptsData) {
        const mappedReceipts = receiptsData.map((rec: any) => ({
          ...rec,
          project_name: Array.isArray(rec.projects) && rec.projects.length > 0
            ? rec.projects[0].name
            : rec.projects?.name || 'Project'
        }));
        setReceipts(mappedReceipts);
      }


    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [ngoId]);

  useEffect(() => {
    if (user?.id && ngoId) {
      fetchData();
    }
  }, [user.id, ngoId, fetchData]);

  // Analytics for the Overview Tab
  const financeOverview = useMemo(() => {
    const totalProjects = projects.length;
    const totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
    const totalRequestedAmount = fundRequests.reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalReceiptsAmount = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
    const approvedRequestsAmount = fundRequests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalSpent = projectExpenditures.reduce((sum, e) => sum + (parseFloat(e.expenditure_amount as string) || 0), 0);

    // 1. Budget Summary Data (Bar Chart)
    const budgetSummaryData = [
      { name: 'Allocated Budget', amount: totalBudget, fill: '#0f766e' }, // Teal
      { name: 'Received', amount: totalReceiptsAmount, fill: '#f97316' }, // Orange
      { name: 'Utilised', amount: totalSpent, fill: '#8b5cf6' }, // Purple
    ];

    // 2. Budget vs Spent Comparison (Optional if needed elsewhere)

    // 3. Monthly Trend Data (Area Chart)
    const monthlyDataMap: { [key: string]: number } = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Initialize with 0
    // months.forEach(m => monthlyDataMap[m] = 0); // Optional: if you want all months shown

    fundRequests.forEach(req => {
      const date = new Date(req.created_at);
      const month = date.toLocaleString('default', { month: 'short' });
      monthlyDataMap[month] = (monthlyDataMap[month] || 0) + req.amount;
    });

    const trendData = Object.keys(monthlyDataMap).map(month => ({
      name: month,
      amount: monthlyDataMap[month]
    }));

    // If no data, provide placeholder for visualization
    if (trendData.length === 0) {
      trendData.push({ name: 'No Data', amount: 0 });
    }

    return {
      totalProjects,
      totalBudget,
      totalRequestedAmount,
      totalReceiptsAmount,
      totalFundReleased: totalReceiptsAmount,
      totalSpent,
      approvedRequestsAmount,
      budgetSummaryData,
      trendData
    };
  }, [projects, fundRequests, receipts, projectExpenditures]);

  // --- Handlers (Identical to original) ---
  const handleViewDoc = async (e: React.MouseEvent, url: string, download: boolean = false) => {
    e.preventDefault();
    if (!url) return;

    if (!url.includes('/storage/v1/object/public/')) {
      window.open(url, '_blank');
      return;
    }

    try {
      const parts = url.split('/storage/v1/object/public/');
      const afterPublic = parts[1];
      const pathParts = afterPublic.split('/');

      if (pathParts.length > 1) {
        const bucketName = pathParts[0];
        const pathInBucket = pathParts.slice(1).join('/');

        const { data } = await supabase.storage.from(bucketName).createSignedUrl(pathInBucket, 60 * 60, {
          download: download
        });

        if (data && data.signedUrl) {
          if (download) {
            const a = document.createElement('a');
            a.href = data.signedUrl;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } else {
            window.open(data.signedUrl, '_blank');
          }
          return;
        }
      }
      window.open(url, '_blank');
    } catch (e) {
      window.open(url, '_blank');
    }
  };

  const handleEditRequest = (request: FundRequest) => {
    setEditingRequestId(request.id);
    setSelectedProject(projects.find(p => p.id === request.project_id) || null);
    setFundRequestForm({
      tranche_number: request.tranche_number.toString(),
      amount: request.amount.toString(),
      purpose: request.purpose,
      attachments: []
    });
    setShowFundRequestModal(true);
  };

  const handleDeleteRequest = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fund request?')) return;
    try {
      const { error } = await supabase.from('fund_requests').delete().eq('id', id);
      if (error) throw error;
      alert('Request deleted successfully');
      await fetchData();
    } catch (err: any) {
      alert(`Error deleting request: ${err.message}`);
    }
  };

  const handleFundRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !ngoId) return;

    setSubmittingRequest(true);
    try {
      const attachmentUrls: string[] = [];
      for (const file of fundRequestForm.attachments) {
        const fileName = `${ngoId}/${selectedProject.id}/fund_request_${Date.now()}_${file.name}`;

        let bucketName = 'fund-request-attachments';
        let uploadError = null;
        const { error: error1 } = await supabase.storage.from(bucketName).upload(fileName, file);

        if (error1) {
          bucketName = 'ngo-documents';
          const { error: error2 } = await supabase.storage.from(bucketName).upload(fileName, file);
          if (error2) uploadError = error2;
        } else {
          uploadError = null;
        }

        if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);

        const { data: { publicUrl } } = supabase.storage.from(bucketName).getPublicUrl(fileName);
        attachmentUrls.push(publicUrl);
      }

      if (editingRequestId) {
        // Keep old attachments if no new ones, or merge them?
        // For now, let's keep existing attachments if no new files are uploaded
        const existingAttachments = fundRequests.find(r => r.id === editingRequestId)?.attachments || [];
        const finalAttachments = attachmentUrls.length > 0 ? [...existingAttachments, ...attachmentUrls] : existingAttachments;

        const { data: updatedData, error: requestError } = await supabase
          .from('fund_requests')
          .update({
            tranche_number: parseInt(fundRequestForm.tranche_number),
            amount: parseFloat(fundRequestForm.amount),
            purpose: fundRequestForm.purpose,
            attachments: finalAttachments,
            status: 'pending',
            clarification_request: null
          })
          .eq('id', editingRequestId)
          .select();

        if (requestError) throw requestError;

        if (!updatedData || updatedData.length === 0) {
          throw new Error('Update failed. This might be due to database permissions (RLS). Please ensure you have run the RLS fix script.');
        }

        alert('Fund request updated successfully!');
      } else {
        const { error: requestError } = await supabase.from('fund_requests').insert({
          project_id: selectedProject.id,
          ngo_id: ngoId,
          tranche_number: parseInt(fundRequestForm.tranche_number),
          amount: parseFloat(fundRequestForm.amount),
          purpose: fundRequestForm.purpose,
          attachments: attachmentUrls,
          status: 'pending'
        });

        if (requestError) throw requestError;
        alert('Fund request submitted successfully!');
      }

      setShowFundRequestModal(false);
      setFundRequestForm({ tranche_number: '', amount: '', purpose: '', attachments: [] });
      setSelectedProject(null);
      setEditingRequestId(null);
      await fetchData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleReceiptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !ngoId) return;

    setSubmittingReceipt(true);
    try {
      let fileUrl = '';
      if (receiptForm.receipt_file) {
        const fileName = `${ngoId}/${selectedProject.id}/receipt_${Date.now()}_${receiptForm.receipt_file.name}`;
        let bucketName = 'fund-request-attachments';
        let uploadError = null;

        const { error: error1 } = await supabase.storage.from(bucketName).upload(fileName, receiptForm.receipt_file);

        if (error1) {
          bucketName = 'ngo-documents';
          const { error: error2 } = await supabase.storage.from(bucketName).upload(fileName, receiptForm.receipt_file);
          if (error2) uploadError = error2;
        } else {
          uploadError = null;
        }

        if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);

        const { data: { publicUrl } } = supabase.storage.from(bucketName).getPublicUrl(fileName);
        fileUrl = publicUrl;
      }

      const { error: saveError } = await supabase.from('fund_receipts').insert({
        project_id: selectedProject.id,
        ngo_id: ngoId,
        tranche_number: parseInt(receiptForm.tranche_number),
        receipt_date: receiptForm.date,
        amount: parseFloat(receiptForm.amount),
        receipt_file_url: fileUrl
      });

      if (saveError) throw saveError;

      alert('Receipt acknowledgement submitted successfully!');
      setShowReceiptModal(false);
      setReceiptForm({ tranche_number: '', date: '', amount: '', receipt_file: null });
      setSelectedProject(null);
      await fetchData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingReceipt(false);
    }
  };


  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="finance-page-container">
      <h1 className="finance-page-title" style={{ marginBottom: '1.5rem' }}>Finance</h1>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="finance-tabs-bar">
        {(['overview', 'budget', 'requests', 'receipts'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`finance-tab-btn ${activeTab === tab ? 'active' : ''}`}
          >
            {tab === 'overview' ? 'Finance Overview' :
              tab === 'budget' ? 'Project Budget' :
                tab === 'requests' ? 'Fund Requests' :
                  tab === 'receipts' ? 'Receipts' : ''}
          </button>
        ))}
      </div>

      {/* Finance Overview Tab - REVAMPED with Graphs */}
      {activeTab === 'overview' && (
        <div>
          {/* Top Metric Cards */}
          <div className="finance-grid-cards">
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center', borderTop: '4px solid #3b82f6' }}>
              <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Projects</h3>
              <p style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', margin: 0 }}>{financeOverview.totalProjects}</p>
            </div>
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center', borderTop: '4px solid #f97316' }}>
              <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Fund Receipts</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: '#f97316' }}>
                {currencyFormatter.format(financeOverview.totalFundReleased)}
              </p>
            </div>
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center', borderTop: '4px solid #8b5cf6' }}>
              <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Total Utilised</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: '#7c3aed' }}>
                {currencyFormatter.format(financeOverview.totalSpent)}
              </p>
            </div>
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center', borderTop: '4px solid #ef4444' }}>
              <h3 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Available Balance</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, color: '#b91c1c' }}>
                {currencyFormatter.format(Math.max(0, financeOverview.totalFundReleased - financeOverview.totalSpent))}
              </p>
            </div>
          </div>

          {/* Graphs Row 1 */}
          <div className="finance-chart-grid">

            {/* Financial Status Bar Chart */}
            <div className="card" style={{ padding: '1.5rem', minHeight: '400px' }}>
              <h3 style={{ marginBottom: '1.5rem', fontSize: '1.1rem', color: 'var(--text)' }}>Financial Status Overview</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={financeOverview.budgetSummaryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" interval={0} tick={<CustomXAxisTick />} height={60} />
                  <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                  <Tooltip formatter={(value) => currencyFormatter.format(Number(value))} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {financeOverview.budgetSummaryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>


          </div>

          {/* Graphs Row 2: Trends */}
          <div className="card" style={{ padding: '1.5rem', minHeight: '400px' }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.1rem', color: 'var(--text)' }}>Monthly Request Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={financeOverview.trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <Tooltip formatter={(value) => currencyFormatter.format(Number(value))} />
                <Area type="monotone" dataKey="amount" stroke="#3b82f6" fillOpacity={1} fill="url(#colorAmount)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Budget Tab */}
      {activeTab === 'budget' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Project-Wise Budget</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              View budget and tranche plan for your assigned projects.
            </p>
          </div>

          {projects.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No projects assigned yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {projects.map((project) => {
                let tranchePlan: any[] = project.tranche_plan as any[];
                if (tranchePlan && typeof tranchePlan === 'string') {
                  try {
                    tranchePlan = JSON.parse(tranchePlan);
                  } catch {
                    tranchePlan = [];
                  }
                }
                if (!Array.isArray(tranchePlan)) tranchePlan = [];

                const hasTranches = tranchePlan.length > 0;

                return (
                  <div key={project.id} className="card">
                    <h3 style={{ marginBottom: '1rem' }}>{project.name}</h3>

                    <div style={{ marginBottom: '1.5rem' }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                        Total Budget:
                      </strong>
                      <p style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary)', margin: 0 }}>
                        {project.budget ? currencyFormatter.format(project.budget) : 'Not set'}
                      </p>
                    </div>

                    {hasTranches ? (
                      <div>
                        <strong style={{ display: 'block', marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
                          Tranche Plan:
                        </strong>
                        <div className="table-container" style={{ marginTop: '0.5rem' }}>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Tranche</th>
                                <th>Amount</th>
                                <th>Planned Date</th>
                                <th>Conditions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tranchePlan.map((tranche: any, index: number) => (
                                <tr key={index}>
                                  <td>Tranche {tranche.tranche_number || index + 1}</td>
                                  <td>{currencyFormatter.format(parseFloat(tranche.amount || 0))}</td>
                                  <td>{tranche.planned_date ? new Date(tranche.planned_date).toLocaleDateString() : 'N/A'}</td>
                                  <td>{tranche.conditions || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <strong style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                          Tranche Plan:
                        </strong>
                        <p style={{ color: 'var(--muted)', margin: 0 }}>Not set</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Fund Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          <div className="card finance-header-card">
            <div>
              <h2>Fund Requests</h2>
              <p style={{ color: 'var(--muted)', marginTop: '0.5rem' }}>
                Request fund release as per approved tranche plan with activity proof and milestone evidence.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (projects.length === 0) {
                  alert('No projects available. Please wait for project assignment.');
                  return;
                }
                setEditingRequestId(null);
                setFundRequestForm({ tranche_number: '', amount: '', purpose: '', attachments: [] });
                setSelectedProject(null);
                setShowFundRequestModal(true);
              }}
            >
              + New Fund Request
            </button>
          </div>

          {fundRequests.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No fund requests submitted yet.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Tranche</th>
                    <th>Amount</th>
                    <th>Purpose</th>
                    <th>Status</th>
                    <th>Review Notes</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fundRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.projects?.name || 'Project'}</td>
                      <td>Tranche {request.tranche_number}</td>
                      <td>{currencyFormatter.format(request.amount)}</td>
                      <td>{request.purpose}</td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          backgroundColor: request.status === 'approved' ? '#10b98115' : request.status === 'rejected' ? '#ef444415' : '#f59e0b15',
                          color: request.status === 'approved' ? '#10b981' : request.status === 'rejected' ? '#ef4444' : '#f59e0b'
                        }}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        {request.clarification_request && (
                          <div style={{ fontSize: '0.875rem', color: '#f59e0b' }}>
                            <strong>Clarification:</strong> {request.clarification_request}
                          </div>
                        )}
                        {request.rejected_reason && (
                          <div style={{ fontSize: '0.875rem', color: '#ef4444' }}>
                            <strong>Rejected:</strong> {request.rejected_reason}
                          </div>
                        )}
                        {!request.clarification_request && !request.rejected_reason && '-'}
                      </td>
                      <td>{new Date(request.created_at).toLocaleDateString()}</td>
                      <td style={{ display: 'flex', gap: '0.5rem' }}>
                        {request.attachments && request.attachments.length > 0 && (
                          <button onClick={(e) => handleViewDoc(e, request.attachments![0])} className="btn btn-sm btn-outline">
                            View Docs
                          </button>
                        )}
                        {(request.status === 'pending' || request.status === 'clarification_requested') && (
                          <>
                            <button onClick={() => handleEditRequest(request)} className="btn btn-sm btn-outline" style={{ color: 'var(--primary)', borderColor: 'var(--primary)' }}>
                              Edit
                            </button>
                            <button onClick={() => handleDeleteRequest(request.id)} className="btn btn-sm btn-outline" style={{ color: 'var(--error)', borderColor: 'var(--error)' }}>
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Fund Request Modal */}
          {showFundRequestModal && (
            <div className="modal-overlay" onClick={() => setShowFundRequestModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>{editingRequestId ? 'Update Fund Request' : 'Submit Fund Request'}</h2>
                <form onSubmit={handleFundRequestSubmit}>
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setSelectedProject(project || null);
                      }}
                      required
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tranche Number *</label>
                    <input
                      type="number"
                      value={fundRequestForm.tranche_number}
                      onChange={(e) => setFundRequestForm({ ...fundRequestForm, tranche_number: e.target.value })}
                      required
                      min="1"
                      placeholder="e.g., 1, 2, 3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Requested Amount (₹) *</label>
                    <input
                      type="number"
                      value={fundRequestForm.amount}
                      onChange={(e) => setFundRequestForm({ ...fundRequestForm, amount: e.target.value })}
                      required
                      placeholder="Enter amount"
                    />
                  </div>
                  <div className="form-group">
                    <label>Purpose / Note *</label>
                    <textarea
                      value={fundRequestForm.purpose}
                      onChange={(e) => setFundRequestForm({ ...fundRequestForm, purpose: e.target.value })}
                      required
                      placeholder="Explain the purpose and provide milestone evidence"
                      rows={4}
                    />
                  </div>
                  <div className="form-group">
                    <label>Attachments (Activity Proof / Milestone Evidence)</label>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setFundRequestForm({ ...fundRequestForm, attachments: files });
                      }}
                    />
                    <div className="help-text">Upload SoE draft, milestone evidence, or activity proof documents</div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowFundRequestModal(false);
                        setSelectedProject(null);
                        setFundRequestForm({ tranche_number: '', amount: '', purpose: '', attachments: [] });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingRequest || !selectedProject}
                    >
                      {submittingRequest ? 'Processing...' : (editingRequestId ? 'Update Request' : 'Submit Request')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Receipts Tab */}
      {activeTab === 'receipts' && (
        <div>
          <div className="card finance-header-card">
            <div>
              <h2>Fund Receipt Acknowledgement</h2>
              <p style={{ color: 'var(--muted)', marginTop: '0.5rem' }}>
                Upload acknowledgment/receipt for released funds.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (projects.length === 0) {
                  alert('No projects available.');
                  return;
                }
                setShowReceiptModal(true);
              }}
            >
              + Upload Receipt
            </button>
          </div>

          {/* Receipt Modal */}
          {showReceiptModal && (
            <div className="modal-overlay" onClick={() => setShowReceiptModal(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2>Upload Receipt Acknowledgement</h2>
                <form onSubmit={handleReceiptSubmit}>
                  <div className="form-group">
                    <label>Select Project *</label>
                    <select
                      value={selectedProject?.id || ''}
                      onChange={(e) => {
                        const project = projects.find(p => p.id === e.target.value);
                        setSelectedProject(project || null);
                      }}
                      required
                    >
                      <option value="">Select project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tranche Number *</label>
                    <input
                      type="number"
                      value={receiptForm.tranche_number}
                      onChange={(e) => setReceiptForm({ ...receiptForm, tranche_number: e.target.value })}
                      required
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Receipt Date *</label>
                    <input
                      type="date"
                      value={receiptForm.date}
                      onChange={(e) => setReceiptForm({ ...receiptForm, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Amount (₹) *</label>
                    <input
                      type="number"
                      value={receiptForm.amount}
                      onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                      required
                      placeholder="Amount must match disbursed value"
                    />
                  </div>
                  <div className="form-group">
                    <label>Receipt PDF *</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setReceiptForm({ ...receiptForm, receipt_file: e.target.files?.[0] || null })}
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setShowReceiptModal(false);
                        setSelectedProject(null);
                        setReceiptForm({ tranche_number: '', date: '', amount: '', receipt_file: null });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingReceipt || !selectedProject}
                    >
                      {submittingReceipt ? 'Uploading...' : 'Upload Receipt'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Receipts List */}
          {receipts.length > 0 ? (
            <div className="card" style={{ marginTop: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Uploaded Receipts ({receipts.length})</h3>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {receipts.map((receipt) => (
                  <div key={receipt.id} style={{
                    padding: '1rem',
                    background: 'var(--background)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <strong>{receipt.project_name}</strong>
                          {receipt.tranche_number && (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              backgroundColor: '#3b82f615',
                              color: '#3b82f6'
                            }}>
                              Tranche {receipt.tranche_number}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <div>
                            <strong>Date:</strong> {new Date(receipt.receipt_date).toLocaleDateString('en-IN')}
                          </div>
                          <div>
                            <strong>Amount:</strong> ₹{parseFloat(receipt.amount).toLocaleString('en-IN')}
                          </div>
                          <div>
                            <strong>Uploaded:</strong> {new Date(receipt.uploaded_at).toLocaleDateString('en-IN')}
                          </div>
                        </div>
                        {receipt.notes && (
                          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            {receipt.notes}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={(e) => handleViewDoc(e, receipt.receipt_file_url)}
                          className="btn btn-sm btn-outline"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => handleViewDoc(e, receipt.receipt_file_url, true)}
                          className="btn btn-sm btn-secondary"
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginTop: '1.5rem', padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No receipts uploaded yet.</p>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default NGOBudget;
